import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Building2, DoorOpen, Sparkles, CheckCircle2, ArrowRight, ArrowLeft,
  Rocket, Home, Bed, MapPin, IndianRupee, Loader2
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';
import { track, trackFirstTime } from '../../services/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMENITIES = ['WiFi', 'AC', 'Geyser', 'Washing Machine', 'Parking', 'Security', 'Laundry', 'Housekeeping', 'Gym', 'Kitchen'];
const ROOM_AMENITIES = ['AC', 'WiFi', 'Attached Bathroom', 'TV', 'Fridge', 'Balcony'];

/**
 * 3-step guided onboarding wizard shown to brand-new users (0 properties).
 *
 * Step 1: Welcome
 * Step 2: Add Property (creates it on "Next")
 * Step 3: Add Room (creates it on "Finish")
 * Done: Toast + onComplete() → layout refreshes property list
 */
const OnboardingWizard = ({ open, onComplete, token, userName }) => {
  const [step, setStep] = useState(0); // 0-welcome, 1-property, 2-room, 3-done
  const [creating, setCreating] = useState(false);
  const [createdProperty, setCreatedProperty] = useState(null);
  const [propForm, setPropForm] = useState({
    name: '', property_type: 'Co-Living',
    address: '', city: '', state: '', pincode: '',
    total_rooms: 1, monthly_rent_range: '₹5000-₹15000',
    amenities: ['WiFi', 'Security'],
  });
  const [roomForm, setRoomForm] = useState({
    room_number: '101', floor: '1', room_type: 'Single',
    total_beds: 1, rent_per_bed: 10000, deposit_amount: 20000,
    amenities: ['AC', 'WiFi'],
  });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkCount, setBulkCount] = useState(10);

  const toggleArr = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const handleCreateProperty = async () => {
    if (!propForm.name || !propForm.address || !propForm.city) {
      toast({ title: 'Please fill required fields', description: 'Name, address, and city are required.', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/properties`, propForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedProperty(res.data);
      setStep(2);
      track('onboarding_step_completed', { step: 'property_created', property_type: propForm.property_type });
      trackFirstTime('first_property_created', { source: 'onboarding_wizard' });
    } catch (e) {
      toast({ title: 'Could not create property', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const handleCreateRoom = async () => {
    if (!createdProperty) return;
    if (!roomForm.room_number || !roomForm.rent_per_bed) {
      toast({ title: 'Please fill required fields', description: 'Room number and rent are required.', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      if (bulkMode) {
        // Use bulk endpoint — create N rooms at once
        const startNum = parseInt(roomForm.room_number) || 101;
        await axios.post(`${API}/properties/${createdProperty.id}/rooms/bulk`, {
          count: bulkCount,
          start_number: startNum,
          floor: roomForm.floor,
          room_type: roomForm.room_type,
          total_beds: parseInt(roomForm.total_beds),
          rent_per_bed: parseFloat(roomForm.rent_per_bed),
          deposit_amount: parseFloat(roomForm.deposit_amount),
          amenities: roomForm.amenities,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/properties/${createdProperty.id}/rooms`, {
          ...roomForm,
          property_id: createdProperty.id,
          rent_per_bed: parseFloat(roomForm.rent_per_bed),
          deposit_amount: parseFloat(roomForm.deposit_amount),
          total_beds: parseInt(roomForm.total_beds),
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setStep(3);
      track('onboarding_step_completed', { step: 'rooms_added', bulk_mode: bulkMode, count: bulkMode ? bulkCount : 1 });
      trackFirstTime('first_room_added', { source: 'onboarding_wizard' });
      setTimeout(() => {
        toast({ title: '🎉 You are all set!', description: bulkMode ? `Added ${bulkCount} rooms — now add tenants.` : 'Add tenants and start collecting rent.' });
        // Seed the first-tenant nudge — Tenants list will auto-open Add Tenant modal
        try { localStorage.setItem('rk_onboarding_jump_to_tenant', '1'); } catch {}
        // Hard redirect first (unmounts wizard cleanly), then flag is set so onComplete doesn't race
        window.location.href = '/manager/people/tenants';
      }, 2000);
    } catch (e) {
      toast({ title: 'Could not create room', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const handleSkip = () => {
    if (window.confirm('Skip setup? You can always add properties and rooms later from the sidebar.')) {
      onComplete();
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden border-0 gap-0"
        data-testid="onboarding-wizard"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Stepper Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Rocket size={20} />
              <DialogTitle className="text-lg font-semibold">Welcome to RentKollect</DialogTitle>
            </div>
            <button
              onClick={handleSkip}
              className="text-xs text-white/80 hover:text-white hover:underline"
              data-testid="onboarding-skip"
            >
              Skip for now
            </button>
          </div>
          <DialogDescription className="sr-only">
            Guided setup wizard to add your first property and room.
          </DialogDescription>
          <div className="flex items-center gap-2">
            {[
              { n: 1, label: 'Welcome', icon: Sparkles },
              { n: 2, label: 'Property', icon: Building2 },
              { n: 3, label: 'Room', icon: DoorOpen },
              { n: 4, label: 'Ready', icon: CheckCircle2 },
            ].map((s, idx) => {
              const isActive = idx === step;
              const isDone = idx < step;
              const Icon = s.icon;
              return (
                <React.Fragment key={s.n}>
                  <div className={`flex items-center gap-1.5 text-xs ${isActive ? 'text-white font-semibold' : isDone ? 'text-white/90' : 'text-white/50'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
                      ${isActive ? 'bg-white text-blue-600' : isDone ? 'bg-green-400 text-white' : 'bg-white/20 text-white/70'}`}>
                      {isDone ? <CheckCircle2 size={12} /> : <Icon size={12} />}
                    </div>
                    <span>{s.label}</span>
                  </div>
                  {idx < 3 && <div className={`h-0.5 flex-1 rounded ${idx < step ? 'bg-green-400' : 'bg-white/20'}`}></div>}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 max-h-[65vh] overflow-y-auto">
          {step === 0 && (
            <div className="text-center py-4" data-testid="onboarding-step-welcome">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <Sparkles size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Hi {userName || 'there'} 👋
              </h3>
              <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                Let's get your rental business set up in under 60 seconds. We'll create your first property and room so you can start onboarding tenants right away.
              </p>
              <div className="grid grid-cols-3 gap-3 mb-6 text-left">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <Building2 className="text-blue-600 mb-2" size={18} />
                  <p className="text-sm font-semibold text-gray-900">Add Property</p>
                  <p className="text-[11px] text-gray-600">Your building or PG</p>
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                  <DoorOpen className="text-purple-600 mb-2" size={18} />
                  <p className="text-sm font-semibold text-gray-900">Add Room</p>
                  <p className="text-[11px] text-gray-600">Rooms/beds with rent</p>
                </div>
                <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                  <CheckCircle2 className="text-green-600 mb-2" size={18} />
                  <p className="text-sm font-semibold text-gray-900">Go Live</p>
                  <p className="text-[11px] text-gray-600">Onboard tenants</p>
                </div>
              </div>
              <Button
                onClick={() => { track('onboarding_step_completed', { step: 'welcome' }); setStep(1); }}
                data-testid="onboarding-start-btn"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8"
              >
                Let's Start <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-property">
              <div className="flex items-center gap-3 pb-2 border-b">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="text-blue-600" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Step 1: Add Your Property</h3>
                  <p className="text-xs text-gray-500">The building, PG, or co-living you manage</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Property Name *</Label>
                  <Input
                    data-testid="onb-prop-name"
                    value={propForm.name}
                    onChange={e => setPropForm({ ...propForm, name: e.target.value })}
                    placeholder="e.g., Sunrise PG"
                  />
                </div>
                <div>
                  <Label className="text-xs">Property Type</Label>
                  <select
                    data-testid="onb-prop-type"
                    value={propForm.property_type}
                    onChange={e => setPropForm({ ...propForm, property_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option>Co-Living</option>
                    <option>Hostel/PG</option>
                    <option>Flat</option>
                    <option>Studio</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Total Rooms</Label>
                  <Input
                    type="number"
                    min="1"
                    value={propForm.total_rooms}
                    onChange={e => setPropForm({ ...propForm, total_rooms: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Address *</Label>
                  <Input
                    data-testid="onb-prop-address"
                    value={propForm.address}
                    onChange={e => setPropForm({ ...propForm, address: e.target.value })}
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <Label className="text-xs">City *</Label>
                  <Input
                    data-testid="onb-prop-city"
                    value={propForm.city}
                    onChange={e => setPropForm({ ...propForm, city: e.target.value })}
                    placeholder="Bangalore"
                  />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input
                    value={propForm.state}
                    onChange={e => setPropForm({ ...propForm, state: e.target.value })}
                    placeholder="Karnataka"
                  />
                </div>
                <div>
                  <Label className="text-xs">Pincode</Label>
                  <Input
                    value={propForm.pincode}
                    onChange={e => setPropForm({ ...propForm, pincode: e.target.value })}
                    placeholder="560001"
                  />
                </div>
                <div>
                  <Label className="text-xs">Rent Range</Label>
                  <Input
                    value={propForm.monthly_rent_range}
                    onChange={e => setPropForm({ ...propForm, monthly_rent_range: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs mb-1.5 block">Amenities</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {AMENITIES.map(a => (
                      <button
                        type="button"
                        key={a}
                        onClick={() => setPropForm({ ...propForm, amenities: toggleArr(propForm.amenities, a) })}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all ${propForm.amenities.includes(a)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-room">
              <div className="flex items-center gap-3 pb-2 border-b">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <DoorOpen className="text-purple-600" size={20} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Step 2: Add Your First Room{bulkMode ? 's' : ''}</h3>
                  <p className="text-xs text-gray-500">
                    Adding to <span className="font-semibold text-gray-800">{createdProperty?.name}</span>
                  </p>
                </div>
              </div>

              {/* Bulk toggle */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                      <Sparkles size={12} className="text-purple-600" />
                      Got many rooms? Add them in bulk!
                    </p>
                    <p className="text-[11px] text-gray-600 mt-0.5">Tap presets, skip typing — create up to 200 rooms in seconds.</p>
                  </div>
                  <button
                    type="button"
                    data-testid="onb-bulk-toggle"
                    onClick={() => setBulkMode(!bulkMode)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${bulkMode ? 'bg-purple-600' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${bulkMode ? 'translate-x-5' : 'translate-x-0'}`}></span>
                  </button>
                </div>
                {bulkMode && (
                  <div className="mt-3 pt-3 border-t border-blue-200/60">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">How many rooms?</span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setBulkCount(Math.max(2, bulkCount - 1))} className="w-7 h-7 rounded-full bg-white border border-gray-300 flex items-center justify-center">−</button>
                        <span className="text-xl font-bold text-purple-700 w-10 text-center" data-testid="onb-bulk-count">{bulkCount}</span>
                        <button type="button" onClick={() => setBulkCount(Math.min(200, bulkCount + 1))} className="w-7 h-7 rounded-full bg-white border border-gray-300 flex items-center justify-center">+</button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[5, 10, 20, 50, 100].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setBulkCount(n)}
                          data-testid={`onb-bulk-preset-${n}`}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${bulkCount === n ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`}
                        >{n}</button>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-600 mt-2">
                      Will create rooms <strong>{roomForm.room_number}</strong> through <strong>{(parseInt(roomForm.room_number) || 101) + bulkCount - 1}</strong> on floor {roomForm.floor}.
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Room Number *</Label>
                  <Input
                    data-testid="onb-room-number"
                    value={roomForm.room_number}
                    onChange={e => setRoomForm({ ...roomForm, room_number: e.target.value })}
                    placeholder="101"
                  />
                </div>
                <div>
                  <Label className="text-xs">Floor</Label>
                  <Input
                    value={roomForm.floor}
                    onChange={e => setRoomForm({ ...roomForm, floor: e.target.value })}
                    placeholder="1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Room Type</Label>
                  <select
                    data-testid="onb-room-type"
                    value={roomForm.room_type}
                    onChange={e => setRoomForm({ ...roomForm, room_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option>Single</option>
                    <option>Double</option>
                    <option>Triple</option>
                    <option>Dormitory</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Total Beds</Label>
                  <Input
                    type="number"
                    min="1"
                    value={roomForm.total_beds}
                    onChange={e => setRoomForm({ ...roomForm, total_beds: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Rent / Bed (₹) *</Label>
                  <Input
                    data-testid="onb-room-rent"
                    type="number"
                    value={roomForm.rent_per_bed}
                    onChange={e => setRoomForm({ ...roomForm, rent_per_bed: e.target.value })}
                    placeholder="10000"
                  />
                </div>
                <div>
                  <Label className="text-xs">Deposit (₹)</Label>
                  <Input
                    type="number"
                    value={roomForm.deposit_amount}
                    onChange={e => setRoomForm({ ...roomForm, deposit_amount: e.target.value })}
                    placeholder="20000"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs mb-1.5 block">Room Amenities</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {ROOM_AMENITIES.map(a => (
                      <button
                        type="button"
                        key={a}
                        onClick={() => setRoomForm({ ...roomForm, amenities: toggleArr(roomForm.amenities, a) })}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all ${roomForm.amenities.includes(a)
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'}`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-10" data-testid="onboarding-step-done">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 mx-auto mb-4 flex items-center justify-center shadow-lg animate-bounce">
                <CheckCircle2 size={40} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">You're all set! 🎉</h3>
              <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                Your first property and room are live. Next up: add tenants, record payments, and explore AI-powered insights.
              </p>
              <div className="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-full">
                <Loader2 size={12} className="animate-spin" /> Loading your dashboard…
              </div>
            </div>
          )}
        </div>

        {/* Footer with Next/Back */}
        {step > 0 && step < 3 && (
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={creating}
              data-testid="onboarding-back-btn"
            >
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
            {step === 1 ? (
              <Button
                data-testid="onboarding-next-property"
                onClick={handleCreateProperty}
                disabled={creating}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {creating ? <><Loader2 className="animate-spin mr-2" size={14} /> Creating…</> : <>Next <ArrowRight size={14} className="ml-1" /></>}
              </Button>
            ) : (
              <Button
                data-testid="onboarding-finish-room"
                onClick={handleCreateRoom}
                disabled={creating}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90"
              >
                {creating ? <><Loader2 className="animate-spin mr-2" size={14} /> Creating…</> : <>Create {bulkMode ? `${bulkCount} Rooms` : 'Room'} <CheckCircle2 size={14} className="ml-1" /></>}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
